import { useMemo } from 'react';
import { useCoinStore } from '../store/coinStore';
import { useChildStore } from '../store';
import { SHOP_THEME_CONFIGS, type ShopThemeConfig } from '../data/shop-theme-configs';
import { VIRTUAL_REWARD_ECONOMY_ENABLED } from '../config/rewards';

/**
 * Returns the active shop theme config for the current child,
 * or null if no theme is equipped.
 *
 * The launch reward loop shelves virtual shop rewards, so existing saved
 * theme equips are ignored while that policy flag is off.
 *
 * Theme items have IDs starting with "theme-" and are tracked
 * in coinStore.equippedItems. Only one can be active at a time
 * (enforced by coinStore.equipItem).
 */
export function useActiveShopTheme(): ShopThemeConfig | null {
  const activeChildId = useChildStore(s => s.activeChildId);
  const equippedItems = useCoinStore(s =>
    activeChildId ? s.equippedItems[activeChildId] : undefined
  );

  return useMemo(() => {
    if (!VIRTUAL_REWARD_ECONOMY_ENABLED) return null;
    if (!equippedItems) return null;
    const themeItemId = equippedItems.find(id => id.startsWith('theme-'));
    if (!themeItemId) return null;
    return SHOP_THEME_CONFIGS[themeItemId] ?? null;
  }, [equippedItems]);
}
